// Uptime-Summary-Store für die Dashboard-Karte "Erreichbarkeit" und die
// Status-Seite: der ENTPRELLTE Zustand aus uptime_state (Backend probed zentral
// pro Tick je Blickwinkel), analog zum DNS-Health-Store.
//
// Datenfluss (seit 03.11.2026 über das Homelab-Protokoll, docs/protokoll.md
// › „Erreichbarkeit, DNS und Bypass über das Protokoll“): jede Zeile (Ziel x
// Blickwinkel) ist eine Instanz uptime.<id>.stand. Das Backend
// (internal/protokoll/erreichbarkeit) gleicht auf Anstoß (jede Probe, jedes
// Anlegen/Ändern/Löschen eines Ziels, gebündelt 250 ms) und alle 30 s ab; der
// Hub verwirft Unverändertes (Totband 20 ms für die Antwortzeit) und schickt
// Deltas. Bis dahin holte jeder Tab /v2-api/uptime/status alle 30 s.
//   - Hier steht die einzige Abo-Stelle (`uptime.*.stand`). Aus einem Stand baut
//     lib/erreichbarkeit-protokoll.ts die Zeile in genau der Form der REST-Antwort;
//     up/down/total zählt der Store wie der Server.
//   - REST (`GET /v2-api/uptime/status`) nur beim Start, als Revalidator
//     (Reconnect, Tab kommt zurück) und als Neuversuch alle 15 s, solange ein
//     Abruf scheiterte. KEIN 30-s-Takt mehr.
//   - Stände und Abrufe mischen wie in agents-summary (ProtokollListe): Stände
//     in-place, ein Stand während eines Abrufs liegt über dessen Antwort, ein
//     `weg` (Ziel gelöscht oder abgeschaltet) hinterlässt einen Grabstein. Die
//     Reihenfolge der Antwort hält `position`.
//
// SWR: nein -- die Liste ist klein und kommt mit dem ersten Stand.

import { getJSON } from "./api";
import {
  uptimeEintraegeAus,
  uptimeEintragAus,
  uptimeIdAusInstanz,
  uptimeZahlen,
  vergleicheUptime,
  type UptimeEintrag,
} from "./erreichbarkeit-protokoll";
import { ProtokollListe } from "./protokoll-liste.svelte";
import { abonniere } from "./protokoll/client.svelte";
import type { UptimeStand } from "./protokoll/schema.gen";
import { skaliere } from "./protokoll/skaliere";
import { registerRevalidator } from "./revalidate.svelte";

export interface UptimeTarget {
  entity: string;
  target_id: string;
  label: string;
  vantage: "external" | "internal" | string;
  url: string;
  health: "up" | "down" | "unknown" | string;
  ok: boolean;
  http_status: number;
  latency_ms: number;
  since?: string;
  consecutive_fails: number;
  flap_count: number;
  last_probe?: string;
  last_error?: string;
  cert_expiry?: string;
  cert_days_left?: number;
}

export interface UptimeSummary {
  targets: UptimeTarget[];
  up: number;
  down: number;
  total: number;
}

const _liste = new ProtokollListe<UptimeEintrag>((e) => e.ziel.entity, vergleicheUptime);
// Ob die Liste etwas weiß (Abruf oder Stand): vorher `undefined`.
let _geladen = $state(false);
let _started = false;
let _unsub: Array<() => void> = [];
// Instanzname -> entity der Zeile: `weg` nennt nur die Instanz.
const _instanzen = new Map<string, string>();

// ── Stände ──────────────────────────────────────────────────────────

function beiStand(instanz: string, daten: Record<string, unknown>): void {
  if (uptimeIdAusInstanz(instanz) === undefined) return;
  const e = uptimeEintragAus(skaliere<UptimeStand>("uptime.stand", daten));
  if (!e) return;
  _instanzen.set(instanz, e.ziel.entity);
  _liste.stand(e);
  // Vor dem ersten Abruf legt laden() die Zeilen über die Antwort; bis dahin
  // wäre die Liste leer, obwohl Stände da sind.
  if (_liste.bereit) _geladen = true;
}

function beiWeg(instanz: string): void {
  const entity = _instanzen.get(instanz);
  if (entity === undefined) return;
  _instanzen.delete(instanz);
  _liste.weg(entity);
}

// ── REST ─────────────────────────────────────────────────────────────

// Nach einem gescheiterten Abruf ein neuer Versuch: ohne Takt käme sonst erst
// der nächste Revalidator.
const NEUVERSUCH_MS = 15_000;
let _neuversuch: ReturnType<typeof setTimeout> | undefined;

// Lauf: zählt Start und Stopp hoch. Eine Antwort, die erst nach dem Stopp
// (oder nach einem neuen Start) ankommt, gehört zu keinem laufenden Store.
let _lauf = 0;

async function laden(): Promise<void> {
  const ab = _liste.nr;
  const lauf = _lauf;
  let rest: UptimeEintrag[] | undefined;
  try {
    const j = await getJSON<UptimeSummary>("/v2-api/uptime/status");
    rest = Array.isArray(j?.targets) ? uptimeEintraegeAus(j.targets) : undefined;
  } catch {
    rest = undefined;
  }
  if (lauf !== _lauf) return;
  try {
    // Was das Protokoll während eines gescheiterten Abrufs gebracht hat, gilt
    // trotzdem.
    _liste.mische(rest, ab);
    if (rest || _liste.neuer(ab)) _geladen = true;
  } finally {
    _liste.bereit = true;
  }
  if (!rest && _started && !_neuversuch) {
    _neuversuch = setTimeout(() => {
      _neuversuch = undefined;
      if (_started) void laden();
    }, NEUVERSUCH_MS);
  }
}

export function startUptimeSummary(): void {
  if (_started) return;
  _started = true;
  _lauf++;
  void laden();
  _unsub.push(abonniere("uptime.*.stand", { rate: "normal" }, { stand: beiStand, weg: beiWeg }));
  // ⚠️ Der Protokoll-Kanal allein reicht NICHT. Reißt die Leitung ab -- und
  // jeder Deploy tut das --, sind verpasste Löschungen weg. registerRevalidator
  // holt beim Reconnect und beim Zurückkommen des Tabs alles frisch.
  _unsub.push(registerRevalidator(laden));
}

export function stopUptimeSummary(): void {
  _started = false;
  _lauf++;
  for (const off of _unsub) off();
  _unsub = [];
  if (_neuversuch) {
    clearTimeout(_neuversuch);
    _neuversuch = undefined;
  }
  _liste.leere();
  _instanzen.clear();
  _geladen = false;
}

// Die Zusammenfassung im Format der REST-Antwort. Neu gebaut, wenn sich die Liste
// oder eine Zeile ändert.
const _summary = $derived.by((): UptimeSummary | undefined => {
  if (!_geladen) return undefined;
  const targets = _liste.items.map((e) => e.ziel);
  return { targets, ...uptimeZahlen(targets) };
});

export function getUptimeSummary(): UptimeSummary | undefined {
  return _summary;
}
